import React, { useCallback, useRef } from 'react';
import { useContextMenu } from 'app/components/ChakraTableList';
import { AudioStatusReasonType, Audio } from 'types/audio';
import {
  AudioDialogsContainer,
  AudioDialogsContainerElement,
} from 'app/components/Song/Audio/AudioDialogs';
import { getMenuOptions } from 'app/components/Song/Audio/AudioTableList/utils';
import { usePlaylistFeatureFlag } from 'featureFlags';
import { StickyTable } from 'app/components/Core/StickyTable/StickyTable';
import { Row } from '@tanstack/react-table';
import { contextMenuHandler } from 'app/components/Table/ContextMenuButton/utils';
import { AudioDialogVariant, AudioTableListProps } from './types';
import { useAudioListActions, useAudioListColumns } from './hooks';
import { AudioTableRow } from './AudioTableRow';
import { AudioGroupedList } from '../AudioList/types';

export const AudioTableList: React.FC<AudioTableListProps> = ({
  audioList,
  audioTypes,
  dispatch,
  downloadAudioHref,
  deleteAudioHref,
  isShareAudioEnabled = false,
  audioFormValidation,
  loading,
  song,
}) => {
  const { playlistFeatureEnabled } = usePlaylistFeatureFlag();
  const dialogRef = useRef<AudioDialogsContainerElement>(null);
  const handleDialogClose = () => dialogRef?.current?.setDialogOpen(undefined);
  const setDialogOpen = (props: AudioDialogVariant) => {
    dialogRef?.current?.setDialogOpen(props);
  };
  const {
    togglePrivateAudio,
    handleSetLatestAudioVersion,
    handleDownload,
    handleRemoveLatestAudioVersion,
    handleRetry,
    handleDelete,
    addToPlaylist,
  } = useAudioListActions({ dispatch, setDialogOpen });

  const ref = useRef<HTMLDivElement>(null);
  const tableKey = 'audios';
  const columns = useAudioListColumns(ref);

  const contextMenuProps = useContextMenu();

  const rowRenderer = useCallback(
    (row: Row<Audio>) => {
      const audio = row.original;
      const options = getMenuOptions({
        audio,
        deleteAudioHref,
        downloadAudioHref,
        isShareAudioEnabled,
        setDialogOpen,
        handleDownload,
        handleRemoveLatestAudioVersion,
        handleSetLatestAudioVersion,
        addToPlaylist:
          playlistFeatureEnabled &&
          audio.statusReason === AudioStatusReasonType.SUCCESS
            ? addToPlaylist
            : undefined,
      });

      return (
        <AudioTableRow
          key={row.id}
          row={row}
          isLoading={({ isAudioLoading, audioPlayerGuid }) =>
            [
              AudioStatusReasonType.TRANSCODING,
              AudioStatusReasonType.UPLOADING,
              AudioStatusReasonType.READY_FOR_UPLOAD,
            ].includes(audio.statusReason) ||
            (isAudioLoading && audio.guid === audioPlayerGuid)
          }
          handleDelete={handleDelete}
          handleRetry={handleRetry}
          togglePrivateAudio={togglePrivateAudio}
          setDialogOpen={setDialogOpen}
          contextHandler={(e) =>
            contextMenuHandler(e, contextMenuProps, options)
          }
        />
      );
    },
    [
      addToPlaylist,
      contextMenuProps,
      deleteAudioHref,
      downloadAudioHref,
      handleDelete,
      handleDownload,
      handleRemoveLatestAudioVersion,
      handleRetry,
      handleSetLatestAudioVersion,
      isShareAudioEnabled,
      playlistFeatureEnabled,
      togglePrivateAudio,
    ]
  );

  return (
    <>
      <AudioDialogsContainer
        ref={dialogRef}
        audioTypes={audioTypes}
        dispatch={dispatch}
        audioFormValidation={audioFormValidation}
        onClose={handleDialogClose}
        song={song}
      />
      <StickyTable<AudioGroupedList>
        tableKey={tableKey}
        ref={ref}
        data={audioList}
        columns={columns}
        rowRenderer={rowRenderer}
        loading={loading}
        contextMenuProps={contextMenuProps}
        getSubRows={(row: any) => {
          return row?.items || ([] as any);
        }}
      />
    </>
  );
};
